import { useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { ChevronDown } from "lucide-react";

const COLLAPSED_HEIGHT = 280;

export function ExpandableHtml({ html }: { html: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  const cleanHtml = useMemo(
    () =>
      typeof window === "undefined"
        ? ""
        : DOMPurify.sanitize(html, {
            ALLOWED_TAGS: ["p", "br", "strong", "em", "b", "i", "u", "ul", "ol", "li", "h2", "h3", "h4", "h5", "h6", "a", "span", "blockquote"],
            ALLOWED_ATTR: ["href", "title", "target", "rel"],
          }),
    [html],
  );

  useEffect(() => {
    if (!ref.current) return;
    setOverflows(ref.current.scrollHeight > COLLAPSED_HEIGHT + 8);
  }, [cleanHtml]);

  return (
    <div className="relative">
      <div
        ref={ref}
        style={{ maxHeight: expanded || !overflows ? "none" : COLLAPSED_HEIGHT }}
        className="prose prose-sm max-w-none overflow-hidden text-foreground/80 prose-headings:font-display prose-headings:text-brand-deep prose-h3:mt-5 prose-h3:mb-2 prose-h3:text-sm prose-h3:font-bold prose-h3:uppercase prose-h3:tracking-wide prose-h4:mt-4 prose-h4:mb-2 prose-h4:text-sm prose-h4:font-bold prose-h4:uppercase prose-h4:tracking-wide prose-p:my-2 prose-li:my-0.5 prose-ul:my-2"
        dangerouslySetInnerHTML={{ __html: cleanHtml }}
      />
      {overflows && !expanded && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-background to-transparent" />
      )}
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-brand transition-colors hover:text-brand-deep"
        >
          {expanded ? "Read less" : "Read more"}
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
      )}
    </div>
  );
}
